/**
 * useRealtimeSuggestedPartners Hook
 *
 * Subscribes to Supabase Realtime for user_profiles table
 * and triggers React Query refetch when new potential partners appear.
 * Updates "Find New Partners" tab instantly when:
 * - New users sign up
 * - Users update their profiles (trade, location, etc.)
 * - Users become available for connection
 *
 * Uses the base useRealtimeSubscription hook for:
 * - Automatic reconnection with exponential backoff
 * - Connection status tracking
 * - Error handling with Sentry integration
 * - Debounced query invalidation
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeSubscription, RealtimeStatus } from './useRealtimeSubscription';

interface UseRealtimeSuggestedPartnersOptions {
  enabled?: boolean;
  onPartnerUpdate?: (partnerData: unknown) => void;
  onStatusChange?: (status: RealtimeStatus) => void;
}

interface UseRealtimeSuggestedPartnersResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Error if status is 'error' */
  error: Error | null;
  /** Manually unsubscribe */
  unsubscribe: () => void;
  /** Manually retry connection */
  retry: () => void;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

export function useRealtimeSuggestedPartners(
  options: UseRealtimeSuggestedPartnersOptions = {}
): UseRealtimeSuggestedPartnersResult {
  const { enabled = true, onPartnerUpdate, onStatusChange } = options;
  const queryClient = useQueryClient();

  // Memoize the change handler to prevent re-subscriptions
  const handlePostgresChange = useCallback(
    (_table: string, payload: { eventType: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
      // Any change to user_profiles could affect suggested partners
      // (new users, profile updates, etc.)

      // Invalidate suggested partners query to refetch
      queryClient.invalidateQueries({ queryKey: ['/api/partners/suggested'] });

      // Call optional callback
      if (onPartnerUpdate) {
        onPartnerUpdate(payload.new || payload.old);
      }
    },
    [queryClient, onPartnerUpdate]
  );

  // Memoize tables configuration
  // Note: We subscribe to all user_profiles changes because:
  // 1. New signups create potential new partners
  // 2. Profile updates change matching criteria
  // 3. The API handles filtering based on user's preferences
  const tables = useMemo(
    () => [
      {
        table: 'user_profiles',
        event: '*' as const,
        // No filter - suggested partners algorithm on backend determines relevance
      },
    ],
    []
  );

  // Use the base subscription hook
  const subscription = useRealtimeSubscription({
    channelName: 'suggested-partners',
    enabled,
    tables,
    onPostgresChange: handlePostgresChange,
    onStatusChange,
    debounceMs: 1000, // Longer debounce since profile updates less critical
  });

  return {
    status: subscription.status,
    error: subscription.error,
    unsubscribe: subscription.unsubscribe,
    retry: subscription.retry,
    reconnectAttempts: subscription.reconnectAttempts,
  };
}

export default useRealtimeSuggestedPartners;
